import { datadogRum } from "@datadog/browser-rum";
import { useInterval } from "@mantine/hooks";
import { AppRoute } from "app";
import { useAtomValue, useSetAtom } from "jotai";
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useNavigate } from "react-router-dom";

import { getDamagedParts } from "api/getDamagedParts";
import { DAMAGED_PARTS_POLL_DURATION } from "app/constants";
import { allDamagedPartsAtom } from "atoms/damageReview";
import { photosScannedAtom } from "atoms/photosUpload";
import { sessionIdAtom } from "atoms/sessionId";
import { loaderTranslationKeyAtom } from "atoms/vehicleAnalysis";
import { useCompleteSession } from "hooks/useCompleteSession";
import { replaceSessionInAppRoute } from "utils/routeHelper";

import { clientConfigAtom } from "../../../atoms/clientConfig";
import { clientIdAtom } from "../../../atoms/clientId";
import { LoaderTranslationKey } from "../constants";

export const usePollDamagedParts = () => {
  const { flowCompletionRedirectUrl, damageReview } = useAtomValue(clientConfigAtom);
  const disabled = damageReview?.disabled || false;

  const navigate = useNavigate();

  const photosScanned = useAtomValue(photosScannedAtom);
  const sessionId = useAtomValue(sessionIdAtom);
  const clientId = useAtomValue(clientIdAtom);
  const setAllDamagedParts = useSetAtom(allDamagedPartsAtom);
  const setLoaderMessage = useSetAtom(loaderTranslationKeyAtom);

  const [countdown, setCountdown] = useState(DAMAGED_PARTS_POLL_DURATION);
  const polling = useInterval(() => setCountdown((timer) => timer - 1), 1000);

  const handleFlowCompletedRedirect = () => {
    const path = flowCompletionRedirectUrl || replaceSessionInAppRoute(AppRoute.FlowCompleted, sessionId);
    navigate(path);
  };

  const { completeSession } = useCompleteSession({ onSuccess: handleFlowCompletedRedirect });

  useQuery("damagedParts", () => getDamagedParts(sessionId, clientId), {
    refetchInterval: 5000,
    refetchOnWindowFocus: true,
    enabled: photosScanned && !disabled,
    onSuccess: ({ damagedParts }) => {
      // Null value means that classification is not yet complete so we need to keep polling until it is not null
      if (damagedParts !== null) {
        datadogRum.addAction("Received damaged parts", { timestamp: Date.now() });

        if (damagedParts.length) {
          setAllDamagedParts(damagedParts);
          navigate(replaceSessionInAppRoute(AppRoute.DamageReview, sessionId));
        } else {
          completeSession();
        }
      } else if (countdown <= 0) {
        datadogRum.addAction("Timeout polling for damaged parts", { timestamp: Date.now() });
        completeSession();
      }
    },
    onError: (error) => {
      datadogRum.addError(error, {
        message: "Failed to poll for damaged parts",
      });

      completeSession();
    },
  });

  useEffect(() => {
    if (photosScanned && !polling.active && !disabled) {
      setLoaderMessage(LoaderTranslationKey.AnalysingDamage);

      polling.start();

      datadogRum.addAction("Start polling for damaged parts", { timestamp: Date.now() });

      return () => {
        polling.stop();
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photosScanned, setLoaderMessage, disabled]);
};
